import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { toUrl } from '../lib/config';

interface Media {
  id: string;
  name: string;
  url: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  isActive: boolean;
}

interface WelcomeModal {
  id: number;
  title: string;
  description: string;
  imageUrl: string;
  isActive: boolean;
  displayOrder: number;
}

const WelcomeModalYonetim: React.FC = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    isActive: true,
    displayOrder: 0
  });

  const [modalId, setModalId] = useState<number | null>(null);
  const [mediaList, setMediaList] = useState<Media[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Image component with fallback
  const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 200 }) => {
    const baseIn = src || '';
    const m = baseIn.match(/([0-9a-fA-F-]{36})$/);
    const raw = m ? `/api/media/${m[1]}`  : baseIn;
    const pref = toUrl(raw);
    const [currentSrc, setCurrentSrc] = React.useState(pref);
    return (
      <img
        src={currentSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff' }}
        crossOrigin="anonymous"
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          const cur = el.getAttribute('src') || '';
          const next = cur === pref ? raw : pref;
          if (next !== cur) el.src = next;
        }}
      />
    );
  };

  // Media listesini yükle
  useEffect(() => {
    fetchMedia();
    fetchActiveModal();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoadingMedia(true);
      const url = toUrl('/api/media/admin');
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        // Backend'den gelen data formatına göre düzenle
        const mediaItems = data.data || data || [];
        setMediaList(mediaItems);
      }
    } catch (err) {
      console.error('Media yükleme hatası:', err);
    } finally {
      setLoadingMedia(false);
    }
  };

  const fetchActiveModal = async () => {
    try {
      const url = toUrl('/api/welcome-modal/active');
      const response = await fetch(url);
      
      if (response.ok) {
        const result = await response.json();
        const modal = result.data;
        setModalId(modal.id);
        setFormData({
          title: modal.title,
          description: modal.description,
          imageUrl: modal.imageUrl,
          isActive: modal.isActive,
          displayOrder: modal.displayOrder
        });
      }
    } catch (err) {
      console.error('Modal yükleme hatası:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const url = modalId 
        ? toUrl(`/api/welcome-modal/${modalId}`)
        : toUrl('/api/welcome-modal');
      
      const response = await fetch(url, {
        method: modalId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('İşlem başarısız oldu');
      }

      const result = await response.json();
      setSuccess(modalId ? 'Modal başarıyla güncellendi!' : 'Modal başarıyla oluşturuldu!');
      
      if (!modalId) {
        setModalId(result.data.id);
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <div className="mb-4">
        <h2 className="fw-bold">Welcome Modal Yönetimi</h2>
        <p className="text-muted">
          Website açılışında gösterilecek modal'ı düzenleyin
        </p>
      </div>

      {/* Başarı Mesajı */}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Hata Mesajı */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            {/* Başlık */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                Başlık <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Örn: Megapark Hastanesi"
                required
                className="form-control-lg"
              />
            </Form.Group>

            {/* Açıklama */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                Açıklama
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Örn: Sağlığınız için yanınızdayız"
                className="form-control-lg"
              />
            </Form.Group>

            {/* Resim Seçimi */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                Resim Seçimi <span className="text-danger">*</span>
              </Form.Label>
              {loadingMedia ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Medya yükleniyor...</span>
                </div>
              ) : (
                <Form.Select
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  required
                  className="form-control-lg"
                >
                  <option value="">Resim Seçiniz</option>
                  {mediaList
                    .filter(media => media.mime.startsWith('image/'))
                    .map(media => (
                      <option key={media.id} value={`/api/media/${media.id}`}>
                        {media.name || media.filename}
                      </option>
                    ))}
                </Form.Select>
              )}
              
              {/* Seçilen Resim Önizleme */}
              {formData.imageUrl && (
                <div className="mt-3">
                  <p className="small text-muted mb-2">Önizleme:</p>
                  <div 
                    className="d-inline-block p-3"
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '2px solid #dee2e6'
                    }}
                  >
                    <ImgWithFallback 
                      src={formData.imageUrl} 
                      alt="Preview"
                      height={200}
                    />
                  </div>
                  <p className="small text-muted mt-2 mb-0">
                    URL: <code>{formData.imageUrl}</code>
                  </p>
                </div>
              )}
            </Form.Group>

            {/* Aktif Durumu */}
            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                name="isActive"
                label="Aktif (Website açılışında göster)"
                checked={formData.isActive}
                onChange={handleChange}
                className="form-check-lg"
              />
              <Form.Text className="text-muted">
                Sadece bir modal aktif olabilir. Bu modal'ı aktif yaparsanız diğerleri otomatik pasif olur.
              </Form.Text>
            </Form.Group>

            {/* Sıralama */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                Sıralama
              </Form.Label>
              <Form.Control
                type="number"
                name="displayOrder"
                value={formData.displayOrder}
                onChange={handleChange}
                min="0"
                className="form-control-lg"
                style={{ maxWidth: '150px' }}
              />
              <Form.Text className="text-muted">
                Birden fazla modal varsa sıralama numarasına göre gösterilir.
              </Form.Text>
            </Form.Group>

            {/* Submit Button */}
            <div className="d-grid gap-2">
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                style={{
                  backgroundColor: '#2cadd2',
                  borderColor: '#2cadd2',
                  fontWeight: '600'
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {modalId ? 'Güncelleniyor...' : 'Oluşturuluyor...'}
                  </>
                ) : (
                  modalId ? 'Modal\'ı Güncelle' : 'Modal Oluştur'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Bilgi Kartı */}
      <Card className="mt-4 border-info">
        <Card.Body>
          <h5 className="fw-bold text-info mb-3">
            <i className="bi bi-info-circle me-2"></i>
            Kullanım Bilgisi
          </h5>
          <ul className="mb-0">
            <li className="mb-2">
              <strong>Başlık:</strong> Modal'da büyük harflerle gösterilecek ana başlık
            </li>
            <li className="mb-2">
              <strong>Açıklama:</strong> Başlığın altında gösterilecek kısa açıklama
            </li>
            <li className="mb-2">
              <strong>Resim:</strong> Sistemdeki medya dosyalarından seçim yapabilirsiniz
            </li>
            <li className="mb-2">
              <strong>Aktif:</strong> Sadece aktif modal website açılışında gösterilir
            </li>
            <li>
              <strong>Sıralama:</strong> Birden fazla modal varsa öncelik sırasını belirler
            </li>
          </ul>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default WelcomeModalYonetim;
